/**
 * Explicit feed tuning, the "More like this / Less like this" signal that social
 * feeds (TikTok, Reddit, YouTube) use to let a reader steer their feed directly,
 * on top of the implicit affinity learned from reactions/saves.
 *
 * A signal map is `category -> score` in a bounded range; positive surfaces a
 * topic, negative buries it. Pure + testable; the app folds these into the
 * personalisation weights at session start.
 */

export const SIGNAL_MIN = -3;
export const SIGNAL_MAX = 3;

function clamp(n: number): number {
  return Math.max(SIGNAL_MIN, Math.min(SIGNAL_MAX, n));
}

/** Apply a "more" (+1) or "less" (-1) nudge to a category, clamped. */
export function bumpTopic(
  signals: Record<string, number>,
  category: string,
  delta: number,
): Record<string, number> {
  if (!category) return signals;
  const next = { ...signals };
  const value = clamp((next[category] ?? 0) + delta);
  if (value === 0) {
    delete next[category];
  } else {
    next[category] = value;
  }
  return next;
}

/**
 * Convert tuning signals into personalisation weight deltas. Positive signals add
 * a strong forward boost; negative signals subtract (burying the topic). Scaled
 * so an explicit tap outweighs a single passive reaction but never nukes variety.
 */
export function tuningWeights(
  signals: Record<string, number>,
  { scale = 1.5 } = {},
): Record<string, number> {
  const weights: Record<string, number> = {};
  for (const [category, score] of Object.entries(signals)) {
    weights[category] = clamp(score) * scale;
  }
  return weights;
}

/** Reader-facing state of a category: "more" | "less" | "neutral". */
export function topicState(signals: Record<string, number>, category: string): "more" | "less" | "neutral" {
  const v = signals[category] ?? 0;
  if (v > 0) return "more";
  if (v < 0) return "less";
  return "neutral";
}
